/* eslint-disable no-useless-catch */
import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { FormattedMessage } from 'react-intl';

import { cloneDeep, find, get, compact } from 'lodash';

import { LoadingView } from '@folio/stripes/components';
import { CalloutContext, withOkapiKy } from '@folio/stripes/core';

import { getRecordObject } from '../../components/util';
import StaffUserForm from './StaffUserForm';
import { toUserAddresses } from '../../components/data/converters/address';
import contactTypes from '../../components/data/static/contactTypes';
import { deliveryFulfillmentValues, USER_TYPES } from '../../constants';
import { resourcesLoaded, showErrorCallout } from '../UserEdit/UserEditHelpers';

class StaffUserCreate extends React.Component {
  static propTypes = {
    stripes: PropTypes.object,
    resources: PropTypes.object,
    history: PropTypes.object,
    location: PropTypes.object,
    match: PropTypes.object,
    okapiKy: PropTypes.func.isRequired,
    mutator: PropTypes.object,
  };

  static contextType = CalloutContext;

  getUserFormValues() {
    const initialFormValues = {
      active: true,
      type: USER_TYPES.STAFF,
      personal: {
        addresses: [],
        firstName: '',
        preferredContactTypeId: (find(contactTypes, { name: 'email' }) || {})
          .id,
      },
      requestPreferences: {
        holdShelf: true,
        delivery: false,
        defaultServicePointId: null,
        defaultDeliveryAddressTypeId: null,
      },
      username: '',
      permissions: [],
      /** kware start edit - Add initial values for service points */
      servicePoints: [],
      preferredServicePoint: '',
      /** kware end edit */
    };

    return initialFormValues;
  }

  getUserFormData() {
    const { resources } = this.props;
    const formData = getRecordObject(
      resources,
      'patronGroups',
      'addressTypes',
      'servicePoints',
      'departments',
    );

    return formData;
  }

  createRequestPreferences = (requestPreferencesData, userId) => {
    const { mutator } = this.props;
    const payload = {
      userId,
      fulfillment: deliveryFulfillmentValues.HOLD_SHELF,
      ...requestPreferencesData,
    };

    return mutator.requestPreferences.POST(payload);
  };

  createUserPassword = async (userId, password) => {
    const { okapiKy } = this.props;

    try {
      const response = await okapiKy.post('authn/credentials', {
        json: {
          userId,
          password,
        },
      });
      return response;
    } catch (error) {
      throw error;
    }
  };

  assignPermissions = async (userId, permissions) => {
    const { mutator } = this.props;

    try {
      // Create permission user record with permissions
      const permissionNames = permissions.map((p) => p.permissionName || p);
      const permUserRecord = await mutator.perms.POST({
        userId,
        permissions: permissionNames,
      });

      return permUserRecord;
    } catch (error) {
      throw error;
    }
  };

  /** kware start edit - Add service points assignment for staff users */
  assignServicePoints = async (userId, servicePoints, preferredServicePoint) => {
    const { okapiKy } = this.props;

    try {
      if (!servicePoints || servicePoints.length === 0) {
        return;
      }

      const payload = {
        userId,
        servicePointsIds: servicePoints.map(sp => sp.id),
        defaultServicePointId: preferredServicePoint !== '-' ? preferredServicePoint : null,
      };

      const response = await okapiKy.post('service-points-users', {
        json: payload,
      });

      return response;
    } catch (error) {
      throw error;
    }
  };
  /** kware end edit */

  create = async ({
    requestPreferences,
    password,
    confirmPassword,
    permissions,
    servicePoints,
    preferredServicePoint,
    ...userFormData
  }) => {
    const {
      mutator,
      history,
      location: { search },
    } = this.props;

    try {
      const userData = cloneDeep(userFormData);
      const user = {
        ...userData,
        id: uuidv4(),
        type: USER_TYPES.STAFF,
      };

      user.personal.addresses = toUserAddresses(user.personal.addresses);
      user.personal.email = user.personal.email.trim();
      user.departments = compact(user.departments);

      if (!user.username) {
        delete user.username;
      }

      // 1. Create user
      await mutator.records.POST(user);

      // 2. Create request preferences
      await this.createRequestPreferences(requestPreferences, user.id);

      // 3. Create password if provided
      if (password) {
        await this.createUserPassword(user.id, password);
      }

      // 4. Assign permissions if provided
      if (permissions && permissions.length > 0) {
        await this.assignPermissions(user.id, permissions);
      } else {
        // Create empty permissions record
        await mutator.perms.POST({ userId: user.id, permissions: [] });
      }

      /** kware start edit - Add service points assignment */
      // 5. Assign service points if provided
      if (servicePoints && servicePoints.length > 0) {
        await this.assignServicePoints(user.id, servicePoints, preferredServicePoint);
      }
      /** kware end edit */

      // 6. Navigate to user detail page
      history.push(`/users/preview/${user.id}${search}`);
    } catch (error) {
      showErrorCallout(error, this.context.sendCallout);
    }
  };

  render() {
    const { history, resources, location } = this.props;

    const profilePictureConfig = get(resources, 'settings.records[0]');

    if (!resourcesLoaded(resources, ['uniquenessValidator'])) {
      return (
        <LoadingView
          data-test-staff-form-page
          paneTitle={<FormattedMessage id="ui-users.crud.createStaffUser" />}
          defaultWidth="100%"
        />
      );
    }

    const formData = this.getUserFormData();

    return (
      <StaffUserForm
        formData={formData}
        initialValues={this.getUserFormValues()}
        onSubmit={this.create}
        onCancel={() => {
          history.push({
            pathname: '/users',
            state: location.state,
            search: location.search,
          });
        }}
        uniquenessValidator={this.props.mutator.uniquenessValidator}
        match={this.props.match}
        location={location}
        history={history}
        stripes={this.props.stripes}
        profilePictureConfig={profilePictureConfig}
      />
    );
  }
}

export default withOkapiKy(StaffUserCreate);
